import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getESectionVO,
    saveESectionPage,
} from '@/services/auditService/auditProgramService';
import {
    E2_PAGE,
    EAPP01_PAGE,
    ES_PAGE,
    E_PAGE_TITLES,
    E_SECTION_INDEX,
    E_SECTION_INDEX_LETTER,
    E_SECTION_INDEX_TITLE,
    E_SECTION_PAGES,
    EProgrammePage,
    ESectionPageCode,
    ESummaryPage,
} from '@/pages/components/audit-program/e-section/constants';
import EsSummaryView from '@/pages/components/audit-program/e-section/EsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type ESectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: ESectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<ESectionPageCode>(['Ei']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: ESectionPageCode): boolean {
    return code === 'E2' || code === 'Eapp01';
}

export default function ESectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Ei',
}: ESectionViewProps) {
    const [pageKey, setPageKey] = useState<ESectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('E');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getESectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of E_SECTION_PAGES) {
                if (code === 'Ei') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load E Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: ESectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveESectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Ei') as {rows?: typeof E_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : E_SECTION_INDEX;
    }, [getContent]);

    const esContent = (getContent('Es') ?? ES_PAGE) as ESummaryPage;
    const e2Content = (getContent('E2') ?? E2_PAGE) as EProgrammePage;
    const eapp01Content = (getContent('Eapp01') ?? EAPP01_PAGE) as EProgrammePage;

    const tabs = useMemo(() => E_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={E_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Ei' ? (
                <SectionIndexView
                    header={header}
                    refCode="Ei"
                    sectionLetter={E_SECTION_INDEX_LETTER}
                    sectionTitle={E_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as ESectionPageCode))}
                />
            ) : code === 'Es' ? (
                <EsSummaryView
                    header={header}
                    page={esContent}
                    refCode="Es"
                    values={pageData.Es ?? {}}
                    onChange={(k, v) => handleFieldChange('Es', k, v)}
                />
            ) : isProgramme(code) ? (
                <G0ProgrammeView
                    header={header}
                    page={code === 'E2' ? e2Content : eapp01Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : null
        ) : null,
    })), [header, pageData, handleFieldChange, indexRows, esContent, e2Content, eapp01Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    E Section — Intangible assets
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as ESectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
